import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDown, Copy, ClipboardPaste, Check } from 'lucide-react';
import { getPlayers, updatePlayer, getScenarios, createScenario, updateScenario, getScheduleDay, getRawTargets, getGpsWeekSummaryScenarios } from '../../api';
import { gymFocusFor } from '../player/PlayerDataContext';
import { downloadBlob, renderReportPdf } from '../../pdfExport';
import { useUrlParam, useUrlParamArray } from '../../hooks/useUrlParam';

// Future Targets — every player's forward-looking day-by-day plan: which GPS load targets apply
// each day of a chosen scenario period, and which gym session (if any) they have that day.
//
// One scenario period is viewed at a time — same "Pick scenario…" picker as WeekSummary.js
// (reusing its exact getGpsWeekSummaryScenarios() endpoint, team-wide periods with id/start/end),
// defaulting to whichever period covers today, same "no period covers today → fall back to the
// most recently started one" rule the week-summary route already uses server-side (mirrored here
// client-side against the same list, not reimplemented against raw scenario rows).
//
// The per-player "overall %" control reuses the scenarios table's existing player_id + intensity
// mechanism (a scenario assignment already supports being scoped to one player instead of the
// whole team, per GPSUpload.js's own "Applies to" picker) rather than adding a new column — a
// player-specific row for the same scenario+start_date as the team row simply overrides intensity
// for that player, exactly like GPSData.js's own target-scaling logic already resolves it
// (mirrored below, not reinvented). The per-day % (2026-08-30) is the same mechanism one level
// down — a day_intensity JSONB map on that same row, `{[date]: percent}` — so a day-specific %
// still requires (and creates, on first edit) a player-specific row, same precedent as the
// overall % already follows.
//
// MD-day scaling is NOT governed by intensity — it's governed by md_minutes (expected minutes
// played that match), same distinction GPSData.js's `enriched` useMemo makes. Neither the overall
// % nor a per-day % control moves MD-day targets; only non-MD (training) days.
//
// allTargets/allScenarioDaysMap come from GET /api/targets/raw (targetResolution.js's
// buildMergedTargetTables) — built-in TARGETS/SCENARIO_DAYS already merged server-side with
// custom scenario templates (load_scenarios), the single source every consumer now shares.
//
// The per-player note reuses players.notes — the same field Players.js's profile editor already
// reads/writes — rather than a new page-scoped column, so a note left here shows up on the
// player's profile too (and vice versa). Flagged in case a plan-scoped note (tied to one
// scenario period, not the player generally) turns out to be what's actually wanted instead.

const METRICS = ['TD', 'RD', 'HID', 'SD', 'SA', 'Expl', 'HMLD'];

// UTC-safe date-add — `new Date(dateStr + 'T00:00:00')` (this file's original approach) parses as
// LOCAL midnight, so toISOString() shifts the result back a day for anyone in a UTC+ timezone
// (confirmed live 2026-08-30, Europe/Amsterdam — every date on this page was rendering one day
// early). Uses the same Date.UTC(y, m-1, d) pattern already established elsewhere in this
// codebase (WeekSchedule.js, TrainerRatings.js) instead of reintroducing the bug's own pattern.
function addDaysStr(dateStr, n) {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + n);
  return dt.toISOString().slice(0, 10);
}
function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}
function scaleTargets(raw, factor) {
  if (!raw) return null;
  if (factor === 1) return raw;
  return Object.fromEntries(
    Object.entries(raw).map(([k, { upper, lower }]) => [k, { upper: Math.round(upper * factor), lower: Math.round(lower * factor) }])
  );
}
function gymLabel(gym) {
  if (!gym || !gym.assigned) return null;
  const parts = [];
  if (gym.upper) parts.push('Upper');
  if (gym.lower) parts.push('Lower');
  if (gym.other) parts.push('Other');
  return parts.length ? parts.join(' + ') : 'Gym';
}

// Effective period for one player at the currently-selected {scenario, startDate} — a
// player-specific row for that same key overrides the team-wide row (the `teamPeriod` passed in
// IS that team-wide row already, so this always resolves to something, never null).
function resolvePeriod(scenarios, playerId, teamPeriod) {
  const custom = scenarios.find(sc =>
    sc.scenario === teamPeriod.scenario && sc.startDate === teamPeriod.startDate &&
    sc.playerId !== 'team' && String(sc.playerId) === String(playerId)
  );
  if (custom) return custom;
  // The real team-wide row (full intensity/target_overrides/md_minutes/day_intensity), NOT
  // `teamPeriod` itself — that argument is the picker-list summary object from
  // getGpsWeekSummaryScenarios() and only ever carries {id, scenario, startDate, endDate,
  // dayCount, isCurrent}. Two bugs from trusting teamPeriod as the fallback, both caught and
  // confirmed live 2026-08-31: (1) it has no `playerId` field, so every downstream
  // `period.playerId !== 'team'` check read `undefined !== 'team'` as true and took the "update"
  // branch against the team row's own id instead of "create" — the first %/day-% edit for any
  // player still on the team default silently overwrote the shared team-wide scenario with that
  // one player's id (this had already happened for real, on both staging and production, before
  // it was caught — repaired by hand, see git history/deploy notes); (2) it has no `intensity`
  // field either, so every such player's targets were being computed against a hardcoded 100%
  // fallback instead of the team's real intensity (e.g. 110%) whenever the two differed.
  const team = scenarios.find(sc =>
    sc.scenario === teamPeriod.scenario && sc.startDate === teamPeriod.startDate && sc.playerId === 'team'
  );
  return team || { ...teamPeriod, playerId: 'team', intensity: 100, target_overrides: {}, md_minutes: 90, day_intensity: {} };
}

// The real team-wide row for the selected period — the base an individualized period inherits
// its hand-edited days from (see resolveDayTargets below). Same "never derive from a partial
// object" rule as resolvePeriod: `selected` is the picker-list summary, not a scenario row.
function resolveTeamRow(scenarios, selected) {
  const team = scenarios.find(sc =>
    sc.scenario === selected.scenario && sc.startDate === selected.startDate && sc.playerId === 'team'
  );
  return team || { ...selected, playerId: 'team', intensity: 100, target_overrides: {}, md_minutes: 90, day_intensity: {} };
}

// Per-day target resolution — MIRRORS targetResolution.js's resolveTargetsForDay (backend, the
// single source of truth); keep the two in sync. Precedence: the period's own hand-typed
// override, then the TEAM period's override for that date re-scaled by this player's % relative
// to the team's % (so a manually edited team day is the base everybody is scaled from), then the
// scenario table scaled by this player's %.
function resolveDayTargets({ raw, period, teamRow, date, position, mdLabel }) {
  const dayFactor = p => (mdLabel === 'MD'
    ? ((p.md_minutes ?? 90) / 90) * 1.1
    : ((p.day_intensity?.[date] ?? p.intensity ?? 100) / 100));
  const factor = dayFactor(period);
  const own = position ? period.target_overrides?.[date]?.[position] : null;
  if (own) return own;
  const teamOverride = position && teamRow && teamRow !== period ? teamRow.target_overrides?.[date]?.[position] : null;
  if (teamOverride) {
    const teamFactor = dayFactor(teamRow);
    const ratio = teamFactor > 0 ? factor / teamFactor : (factor > 0 ? 1 : 0);
    return scaleTargets(teamOverride, ratio);
  }
  return scaleTargets(raw, factor);
}

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '18px 20px', marginBottom: 18 };

function NotesBox({ player, onSaved }) {
  const [value, setValue] = useState(player.notes || '');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updatePlayer(player.id, { ...player, notes: value });
      setDirty(false);
      onSaved(updated);
    } finally { setSaving(false); }
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <textarea
        value={value}
        onChange={e => { setValue(e.target.value); setDirty(true); }}
        placeholder="Note about this player…"
        rows={2}
        style={{ width: '100%', resize: 'vertical', padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 12.5, fontFamily: 'inherit' }}
      />
      {dirty && (
        <div style={{ marginTop: 6 }}>
          <button onClick={save} disabled={saving}
            style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
            {saving ? 'Saving…' : 'Save note'}
          </button>
        </div>
      )}
    </div>
  );
}

// One day's %-cell: shows the effective value (day-specific if set, else the period's overall %),
// with a small inline editor toggled by a pencil-style "Edit" link — same click-to-edit shape the
// period-level control already uses, just scoped to one row instead of the whole card.
function DayPercentCell({ playerId, period, date, isEditing, editValue, setEditValue, onStartEdit, onCancelEdit, onSave, saving }) {
  const hasOverride = period.day_intensity?.[date] != null;
  if (isEditing) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
        <input type="number" min={50} max={120} step={5} value={editValue}
          onChange={e => setEditValue(e.target.value)}
          placeholder={String(period.intensity ?? 100)}
          style={{ width: 48, padding: '3px 5px', borderRadius: 5, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 10.5 }} />
        <button onClick={onSave} disabled={saving} title="Save"
          style={{ padding: '2px 6px', borderRadius: 5, border: 'none', background: '#2563eb', color: '#fff', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>✓</button>
        <button onClick={onCancelEdit} title="Cancel"
          style={{ padding: '2px 6px', borderRadius: 5, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-muted)', fontSize: 10, cursor: 'pointer' }}>✕</button>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
      <span style={{ color: hasOverride ? '#60a5fa' : 'var(--text-dimmed)', fontWeight: hasOverride ? 700 : 400 }}>
        {hasOverride ? `${period.day_intensity[date]}%` : `${period.intensity ?? 100}%`}
      </span>
      <button onClick={onStartEdit} title="Edit this day's %"
        style={{ padding: '1px 4px', borderRadius: 4, border: 'none', background: 'transparent', color: 'var(--text-dimmed)', fontSize: 10, cursor: 'pointer' }}>✎</button>
    </div>
  );
}

function ScenarioPeriodCard({
  playerId, playerPosition, period, teamPeriod, teamRow, scheduleDays, allTargets, dates,
  editingKey, editValue, setEditValue, onStartEdit, onCancelEdit, onSaveIntensity, saving,
  editingDayKey, editDayValue, setEditDayValue, onStartDayEdit, onCancelDayEdit, onSaveDayIntensity, savingDay,
  onCopyPercents, onPastePercents, justCopied, canPaste, pasting, clipboardSourceName,
}) {
  const key = `${playerId}|${teamPeriod.scenario}|${teamPeriod.startDate}`;
  const isCustom = period.playerId !== 'team';
  const isEditing = editingKey === key;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>Scenario {teamPeriod.scenario}</span>
          <span style={{ fontSize: 11, color: 'var(--text-dimmed)', marginLeft: 8 }}>{fmtDate(teamPeriod.startDate)} – {fmtDate(teamPeriod.endDate)}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 10.5, fontWeight: 600, padding: '3px 8px', borderRadius: 99, background: isCustom ? 'rgba(59,130,246,0.15)' : 'var(--border)', color: isCustom ? '#60a5fa' : 'var(--text-muted)' }}>
            {isCustom ? `Custom: ${period.intensity ?? 100}%` : `Team default: ${period.intensity ?? 100}%`}
          </span>
          {isEditing ? (
            <>
              <input type="number" min={50} max={120} step={5} value={editValue}
                onChange={e => setEditValue(Number(e.target.value))}
                style={{ width: 56, padding: '5px 6px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 11 }} />
              <button onClick={onSaveIntensity} disabled={saving}
                style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={onCancelEdit} style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer' }}>
                Cancel
              </button>
            </>
          ) : (
            <button onClick={onStartEdit}
              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
              Edit overall %
            </button>
          )}
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5 }}>
          <thead>
            <tr style={{ textAlign: 'right', color: 'var(--text-dimmed)', fontSize: 9.5, textTransform: 'uppercase' }}>
              <th style={{ textAlign: 'left', padding: '5px 7px' }}>Date</th>
              <th style={{ padding: '5px 7px' }}>Day</th>
              <th style={{ padding: '5px 7px' }}>Gym</th>
              <th style={{ padding: '5px 7px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                  <span>%</span>
                  <button onClick={onCopyPercents} title="Copy this player's % for the whole week"
                    style={{ padding: 2, borderRadius: 4, border: 'none', background: justCopied ? 'rgba(52,211,153,0.18)' : 'transparent', color: justCopied ? '#34d399' : 'var(--text-dimmed)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    {justCopied ? <Check size={11} /> : <Copy size={11} />}
                  </button>
                  <button onClick={onPastePercents} disabled={!canPaste || pasting}
                    title={canPaste ? `Paste ${clipboardSourceName}'s % onto this player` : 'Copy a player\'s % for this scenario first'}
                    style={{ padding: 2, borderRadius: 4, border: 'none', background: 'transparent', color: canPaste ? '#60a5fa' : 'var(--text-dimmed)', cursor: canPaste && !pasting ? 'pointer' : 'not-allowed', opacity: canPaste ? 1 : 0.4, display: 'flex', alignItems: 'center' }}>
                    <ClipboardPaste size={11} />
                  </button>
                </div>
              </th>
              {METRICS.map(m => <th key={m} style={{ padding: '5px 7px' }}>{m}</th>)}
            </tr>
          </thead>
          {(() => {
            // Precomputed once per day so the same resolved `targets` (day-% override, absolute
            // target_overrides, MD's md_minutes scaling — whichever applies) backs both the row
            // itself and the Total row's sum below, instead of recomputing it twice.
            const rows = dates.map(({ mdLabel, date }) => {
              const raw = playerPosition ? allTargets[teamPeriod.scenario]?.[mdLabel]?.[playerPosition] : null;
              const targets = resolveDayTargets({ raw, period, teamRow, date, position: playerPosition, mdLabel });
              const sched = scheduleDays[date];
              const gym = sched ? gymFocusFor(sched, playerId) : null;
              return { mdLabel, date, targets, gLabel: gymLabel(gym) };
            });
            const totals = {};
            METRICS.forEach(m => {
              const withValue = rows.filter(r => r.targets?.[m]);
              totals[m] = withValue.length
                ? { lower: withValue.reduce((s, r) => s + r.targets[m].lower, 0), upper: withValue.reduce((s, r) => s + r.targets[m].upper, 0) }
                : null;
            });

            return (
              <>
                <tbody>
                  {rows.map(({ mdLabel, date, targets, gLabel }) => {
                    const dayKey = `${playerId}|${date}`;
                    return (
                      <tr key={date} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ textAlign: 'left', padding: '6px 7px', color: 'var(--text-primary)' }}>{fmtDate(date)}</td>
                        <td style={{ textAlign: 'right', padding: '6px 7px', color: 'var(--text-muted)', fontWeight: mdLabel === 'MD' ? 700 : 400 }}>{mdLabel}</td>
                        <td style={{ textAlign: 'right', padding: '6px 7px', color: gLabel ? '#34d399' : 'var(--text-dimmed)' }}>{gLabel || '—'}</td>
                        <td style={{ textAlign: 'right', padding: '6px 7px' }}>
                          {mdLabel === 'MD' ? (
                            <span style={{ color: 'var(--text-dimmed)' }} title="MD scales by minutes played, not %">—</span>
                          ) : (
                            <DayPercentCell
                              playerId={playerId} period={period} date={date}
                              isEditing={editingDayKey === dayKey}
                              editValue={editDayValue} setEditValue={setEditDayValue}
                              onStartEdit={() => onStartDayEdit(period, date)}
                              onCancelEdit={onCancelDayEdit}
                              onSave={() => onSaveDayIntensity(period, date)}
                              saving={savingDay}
                            />
                          )}
                        </td>
                        {METRICS.map(m => (
                          <td key={m} style={{ textAlign: 'right', padding: '6px 7px', color: 'var(--text-secondary)' }}>
                            {targets?.[m] ? `${targets[m].lower.toLocaleString()}–${targets[m].upper.toLocaleString()}` : '—'}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--border-strong)' }}>
                    <td colSpan={4} style={{ textAlign: 'left', padding: '7px 7px', fontWeight: 700, color: 'var(--text-primary)' }}>Total</td>
                    {METRICS.map(m => (
                      <td key={m} style={{ textAlign: 'right', padding: '7px 7px', fontWeight: 700, color: 'var(--text-primary)' }}>
                        {totals[m] ? `${totals[m].lower.toLocaleString()}–${totals[m].upper.toLocaleString()}` : '—'}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </>
            );
          })()}
        </table>
      </div>
    </div>
  );
}

// Player filter — a dropdown of checkboxes with explicit "Select all" / "Clear all" bulk
// actions. Neither of this app's existing multi-select controls (components/MultiSelect.js,
// InMatchAnalysis.js's PlayerMultiSelect) offers a real select-all beyond "empty = all" — this
// one keeps that same empty-means-all convention for the closed/default state, but adds the two
// explicit actions on top so a coach can start from "everyone" and remove a few, or start from
// "nobody" and add a few, either way.
//
// "Nobody selected" can't be represented by an empty array — empty already means "everyone" for
// the closed/default state (and for useUrlParamArray, which collapses [] and a missing param to
// the same thing). NONE_SELECTED is a sentinel array value standing in for that explicit
// zero-players state so "Clear all" is distinguishable from "no filter applied".
const NONE_SELECTED = '__none__';

function PlayerFilter({ players, selectedIds, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    const h = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const isNoneState = selectedIds.length === 1 && selectedIds[0] === NONE_SELECTED;
  // The ids actually checked right now, with the implicit "empty = all" state expanded out —
  // toggling always operates on this explicit list so unchecking one player out of "all" removes
  // just that one instead of collapsing the whole selection down to it.
  const effectiveIds = isNoneState ? [] : selectedIds.length === 0 ? players.map(p => String(p.id)) : selectedIds;

  const toggle = id => {
    const next = effectiveIds.includes(id) ? effectiveIds.filter(v => v !== id) : [...effectiveIds, id];
    if (next.length === players.length) onChange([]);
    else if (next.length === 0) onChange([NONE_SELECTED]);
    else onChange(next);
  };
  const selectAll = () => onChange([]);
  const clearAll = () => onChange([NONE_SELECTED]);

  const label = isNoneState
    ? 'No players'
    : selectedIds.length === 0
      ? 'All players'
      : selectedIds.length === 1
        ? players.find(p => String(p.id) === selectedIds[0])?.name || '1 player'
        : `${selectedIds.length} players`;

  return (
    <div ref={rootRef} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px 7px 12px', borderRadius: 8,
        border: `1px solid ${selectedIds.length > 0 ? '#3b82f6' : 'var(--border-strong)'}`,
        background: selectedIds.length > 0 ? 'rgba(59,130,246,0.1)' : 'var(--bg-card)',
        color: selectedIds.length > 0 ? '#93c5fd' : 'var(--text-primary)',
        fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', minWidth: 180,
      }}>
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.15s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 300,
          background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 10,
          minWidth: 240, maxHeight: 340, overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
          <div style={{ display: 'flex', gap: 6, padding: '8px 10px', borderBottom: '1px solid var(--border)', position: 'sticky', top: 0, background: 'var(--bg-card)' }}>
            <button onClick={selectAll} style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
              Select all
            </button>
            <button onClick={clearAll} style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
              Clear all
            </button>
          </div>
          {players.map(p => {
            const id = String(p.id);
            const checked = effectiveIds.includes(id);
            return (
              <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', cursor: 'pointer',
                fontSize: 12.5, color: 'var(--text-primary)',
                background: checked ? 'rgba(59,130,246,0.07)' : 'transparent' }}>
                <input type="checkbox" checked={checked} onChange={() => toggle(id)} style={{ accentColor: '#3b82f6', flexShrink: 0 }} />
                {p.name}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Scenario picker — same shape as WeekSummary.js's "Pick scenario…" dropdown, reusing its exact
// getGpsWeekSummaryScenarios() list (team-wide periods, each already carrying start/end dates and
// an isCurrent flag computed server-side with the custom-scenario day-count merge applied).
function ScenarioPicker({ list, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    const h = e => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);
  const currentIdx = list.findIndex(s => s.id === selected?.id);
  const goPrevious = () => { const next = list[currentIdx + 1]; if (next) onSelect(next.id); };
  const goCurrent = () => { const cur = list.find(s => s.isCurrent); onSelect(cur ? cur.id : null); };

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <button onClick={goPrevious} disabled={currentIdx < 0 || currentIdx >= list.length - 1}
        style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 12, cursor: currentIdx < list.length - 1 ? 'pointer' : 'default', opacity: currentIdx >= list.length - 1 || currentIdx < 0 ? 0.4 : 1 }}>
        Previous
      </button>
      <button onClick={goCurrent} disabled={!!selected?.isCurrent}
        style={{ padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: selected?.isCurrent ? '#3b82f6' : 'var(--bg-card)', color: selected?.isCurrent ? '#fff' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
        Current
      </button>
      <div ref={rootRef} style={{ position: 'relative' }}>
        <button onClick={() => setOpen(o => !o)} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' }}>
          Pick scenario… <ChevronDown size={12} />
        </button>
        {open && (
          <div style={{ position: 'absolute', top: '110%', right: 0, background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 300, maxHeight: 280, overflowY: 'auto', minWidth: 220 }}>
            {list.map(s => (
              <button key={s.id} onClick={() => { onSelect(s.id); setOpen(false); }}
                style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', borderBottom: '1px solid var(--border)', background: s.id === selected?.id ? 'var(--bg-app)' : 'transparent', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}>
                {s.scenario} <span style={{ color: 'var(--text-dimmed)', fontSize: 10.5 }}>{fmtDate(s.startDate)}–{fmtDate(s.endDate)}</span>
                {s.isCurrent && <span style={{ marginLeft: 6, fontSize: 9.5, color: '#34d399' }}>· current</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function FutureTargets() {
  const [players, setPlayers] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [rawTables, setRawTables] = useState({ targets: {}, scenarioDays: {} });
  const [pickerList, setPickerList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scheduleDays, setScheduleDays] = useState({});
  const [scenarioIdRaw, setScenarioIdRaw] = useUrlParam('scenario_id', '');
  const scenarioId = scenarioIdRaw === '' ? null : Number(scenarioIdRaw);
  const setScenarioId = id => setScenarioIdRaw(id == null ? '' : id);
  const [editingKey, setEditingKey] = useState(null); // `${playerId}|${scenario}|${startDate}`
  const [editValue, setEditValue] = useState(100);
  const [editingDayKey, setEditingDayKey] = useState(null); // `${playerId}|${date}`
  const [editDayValue, setEditDayValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingDay, setSavingDay] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Copy/paste one player's whole-week % pattern onto another — scoped to {scenario, startDate}
  // so a stale copy from a different period (different set of dates) can't silently misapply
  // after switching the scenario picker; pastingId is the one player currently mid-save so only
  // that row's paste button shows a busy state, not every row's.
  const [clipboard, setClipboard] = useState(null); // { scenario, startDate, sourcePlayerName, values: {[date]: percent} }
  const [justCopiedId, setJustCopiedId] = useState(null);
  const [pastingId, setPastingId] = useState(null);
  const contentRef = useRef(null);
  const [selectedIds, setSelectedIds] = useUrlParamArray('players'); // [] = everyone, matching this app's existing multi-select convention

  const loadScenarios = () =>
    getScenarios().then(scens => setScenarios((scens || []).map(s => ({ ...s, startDate: s.start_date?.slice(0, 10), playerId: s.player_id }))));

  useEffect(() => {
    Promise.all([getPlayers(), getScenarios(), getRawTargets(), getGpsWeekSummaryScenarios()])
      .then(([pl, scens, raw, picker]) => {
        setPlayers((pl || []).filter(p => p.active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        setScenarios((scens || []).map(s => ({ ...s, startDate: s.start_date?.slice(0, 10), playerId: s.player_id })));
        setRawTables({ targets: raw?.targets || {}, scenarioDays: raw?.scenarioDays || {} });
        setPickerList(picker || []);
      })
      .finally(() => setLoading(false));
  }, []);

  // Merged built-in + custom lookup maps (server-side merge — targetResolution.js's
  // buildMergedTargetTables — a scenario period can be built from a self-made scenario just as
  // easily as a built-in one, see file header comment).
  const allTargets = rawTables.targets;
  const allScenarioDaysMap = rawTables.scenarioDays;

  const selected = useMemo(() => {
    if (!pickerList.length) return null;
    if (scenarioId) return pickerList.find(s => s.id === scenarioId) || null;
    return pickerList.find(s => s.isCurrent) || pickerList[0] || null;
  }, [pickerList, scenarioId]);

  const dates = useMemo(() => {
    if (!selected) return [];
    const days = allScenarioDaysMap[selected.scenario] || [];
    return days.map((mdLabel, i) => ({ mdLabel, date: addDaysStr(selected.startDate, i) }));
  }, [selected, allScenarioDaysMap]);

  const visiblePlayers = useMemo(() => {
    if (selectedIds.length === 0) return players;
    if (selectedIds.length === 1 && selectedIds[0] === NONE_SELECTED) return [];
    return players.filter(p => selectedIds.includes(String(p.id)));
  }, [players, selectedIds]);

  const teamRow = useMemo(() => (selected ? resolveTeamRow(scenarios, selected) : null), [scenarios, selected]);

  const periodByPlayer = useMemo(() => {
    const map = new Map();
    if (!selected) return map;
    for (const p of visiblePlayers) map.set(p.id, resolvePeriod(scenarios, p.id, selected));
    return map;
  }, [visiblePlayers, scenarios, selected]);

  useEffect(() => {
    const ds = dates.map(d => d.date);
    if (!ds.length) { setScheduleDays({}); return; }
    let cancelled = false;
    Promise.all(ds.map(d => getScheduleDay(d).then(r => [d, r]).catch(() => [d, null])))
      .then(entries => { if (!cancelled) setScheduleDays(Object.fromEntries(entries)); });
    return () => { cancelled = true; };
  }, [dates]);

  // When `period` is still the TEAM row, saving a % here CREATES this player's own row — and it
  // must start with an EMPTY target_overrides. The team's overrides are absolute hand-typed
  // numbers; copying them into the new row would freeze this player on the team's values for
  // those days and make the very % being edited a no-op there. Left empty, the row inherits the
  // team's edited days as its base and scales them by his % (targetResolution.js's
  // resolveTargetsForDay, rule 2). Day-% inheritance is different and IS copied: it is a
  // multiplier, so carrying it over reproduces the team plan exactly.
  const inheritedOverrides = period => (period.playerId === 'team' ? {} : (period.target_overrides || {}));

  const startEdit = (playerId, period) => { setEditingKey(`${playerId}|${selected.scenario}|${selected.startDate}`); setEditValue(period.intensity ?? 100); };

  const saveIntensity = async (playerId, period) => {
    setSaving(true);
    try {
      const payload = {
        scenario: selected.scenario, start_date: selected.startDate, player_id: playerId,
        intensity: editValue, md_minutes: period.md_minutes ?? 90,
        target_overrides: inheritedOverrides(period), day_intensity: period.day_intensity || {},
      };
      if (period.playerId !== 'team') await updateScenario(period.id, payload);
      else await createScenario(payload);
      await loadScenarios();
      setEditingKey(null);
    } finally { setSaving(false); }
  };

  // Copy captures the EFFECTIVE % for every non-MD day (a day-specific override if one exists,
  // else whatever the period's overall % currently resolves to for that day) — not just the days
  // that happen to already have an explicit override — so pasting reproduces the player's whole
  // observed week, not a partial pattern.
  const copyPercents = (player, period) => {
    const values = {};
    dates.forEach(({ mdLabel, date }) => {
      if (mdLabel === 'MD') return;
      values[date] = period.day_intensity?.[date] ?? period.intensity ?? 100;
    });
    setClipboard({ scenario: selected.scenario, startDate: selected.startDate, sourcePlayerName: player.name, values });
    setJustCopiedId(player.id);
    setTimeout(() => setJustCopiedId(id => (id === player.id ? null : id)), 1500);
  };

  const pastePercents = async (player, period) => {
    if (!clipboard || clipboard.scenario !== selected.scenario || clipboard.startDate !== selected.startDate) return;
    setPastingId(player.id);
    try {
      const payload = {
        scenario: selected.scenario, start_date: selected.startDate, player_id: player.id,
        intensity: period.intensity ?? 100, md_minutes: period.md_minutes ?? 90,
        target_overrides: inheritedOverrides(period), day_intensity: clipboard.values,
      };
      if (period.playerId !== 'team') await updateScenario(period.id, payload);
      else await createScenario(payload);
      await loadScenarios();
    } finally { setPastingId(null); }
  };

  const handleNoteSaved = updatedPlayer => {
    setPlayers(prev => prev.map(p => (p.id === updatedPlayer.id ? { ...p, notes: updatedPlayer.notes } : p)));
  };

  // Same captureBlocks/packBlocksIntoPages/A4 pipeline as every other export in the app
  // (src/pdfExport.js) — each player's whole card (name, note, and their period table) is marked
  // as one data-report-block, so the packer keeps it together on one page rather than cutting a
  // table across a page boundary; a block only ever gets hard-sliced if a single player's card is
  // physically taller than one A4 page on its own (see pdfExport.js).
  const handleExport = async () => {
    if (!contentRef.current) return;
    setExporting(true);
    try {
      const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--bg-app').trim() || '#0f172a';
      const arrayBuffer = await renderReportPdf(contentRef.current, bgColor, { scale: 1.5, imageFormat: 'JPEG', imageQuality: 0.85 });
      const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
      const dateStr = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `future-targets-${selected?.startDate || dateStr}.pdf`);
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-dimmed)' }}>Loading…</div>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>Future Targets</h1>
          <p style={{ fontSize: 12, color: 'var(--text-dimmed)', margin: '4px 0 0' }}>
            Every player's day-by-day plan for one scenario period — GPS targets and gym sessions, before they happen.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <PlayerFilter players={players} selectedIds={selectedIds} onChange={setSelectedIds} />
          <ScenarioPicker list={pickerList} selected={selected} onSelect={setScenarioId} />
          <button
            onClick={handleExport}
            disabled={exporting || !visiblePlayers.length}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 9,
              border: '1px solid var(--border-strong)',
              background: exporting ? 'var(--border)' : 'var(--bg-card)',
              color: exporting ? 'var(--text-dimmed)' : 'var(--text-secondary)',
              fontSize: 13, fontWeight: 600, cursor: exporting ? 'not-allowed' : 'pointer',
            }}
          >
            {exporting ? 'Exporting…' : 'PDF Export'}
          </button>
        </div>
      </div>

      {!selected ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>No team scenario found — create one in the Upload zone first.</div>
      ) : (
        <div ref={contentRef}>
          {visiblePlayers.length === 0 && (
            <div style={{ ...card, textAlign: 'center', color: 'var(--text-dimmed)', padding: 40 }}>No players match the current selection.</div>
          )}
          {visiblePlayers.map(player => {
            const period = periodByPlayer.get(player.id);
            return (
              <div key={player.id} data-report-block="true" style={card}>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 10 }}>
                  {player.name}
                  {player.position && <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-dimmed)', marginLeft: 8 }}>{player.position}</span>}
                </div>

                <NotesBox player={player} onSaved={handleNoteSaved} />

                {period && (
                  <ScenarioPeriodCard
                    playerId={player.id}
                    playerPosition={player.position}
                    period={period}
                    teamPeriod={selected}
                    teamRow={teamRow}
                    scheduleDays={scheduleDays}
                    allTargets={allTargets}
                    dates={dates}
                    editingKey={editingKey}
                    editValue={editValue}
                    setEditValue={setEditValue}
                    onStartEdit={() => startEdit(player.id, period)}
                    onCancelEdit={() => setEditingKey(null)}
                    onSaveIntensity={() => saveIntensity(player.id, period)}
                    saving={saving}
                    editingDayKey={editingDayKey}
                    editDayValue={editDayValue}
                    setEditDayValue={setEditDayValue}
                    onStartDayEdit={(p, date) => {
                      setEditingDayKey(`${player.id}|${date}`);
                      setEditDayValue(p.day_intensity?.[date] ?? '');
                    }}
                    onCancelDayEdit={() => setEditingDayKey(null)}
                    onSaveDayIntensity={async (p, date) => {
                      setSavingDay(true);
                      try {
                        const nextDayIntensity = { ...(p.day_intensity || {}) };
                        if (editDayValue === '' || editDayValue == null) delete nextDayIntensity[date];
                        else nextDayIntensity[date] = Number(editDayValue);
                        const payload = {
                          scenario: selected.scenario, start_date: selected.startDate, player_id: player.id,
                          intensity: p.intensity ?? 100, md_minutes: p.md_minutes ?? 90,
                          target_overrides: inheritedOverrides(p), day_intensity: nextDayIntensity,
                        };
                        if (p.playerId !== 'team') await updateScenario(p.id, payload);
                        else await createScenario(payload);
                        await loadScenarios();
                        setEditingDayKey(null);
                      } finally { setSavingDay(false); }
                    }}
                    savingDay={savingDay}
                    onCopyPercents={() => copyPercents(player, period)}
                    onPastePercents={() => pastePercents(player, period)}
                    justCopied={justCopiedId === player.id}
                    canPaste={!!clipboard && clipboard.scenario === selected.scenario && clipboard.startDate === selected.startDate}
                    pasting={pastingId === player.id}
                    clipboardSourceName={clipboard?.sourcePlayerName}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
